import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { getUserProfile, UserProfile } from '../services/authService';
import {
    listReports,
    listSavedSettings,
    fetchReportPdf,
    fetchRevisionPdf,
    deleteReport,
    deleteRevision,
    hasConfiguredLocalServer,
    getStorageMode,
    LocalServerError,
    ReportListItem,
    SavedSettingListItem
} from '../services/localServerService';
import {
    listCloudReports,
    listCloudSavedSettings,
    fetchCloudReportPdfUrl,
    fetchCloudSettingsPdfUrl,
    deleteCloudReport,
    deleteCloudSavedSettings,
    ensureCloudRetentionIfDue
} from '../services/cloudStorageService';
import type { PdfWorkflowMode } from '../services/authService';
import { logger } from '../utils/logger';
import { EngineeringLoadingScreen } from '../components/EngineeringLoadingScreen';
import '../../public/styles/main.css';

const PAGE_SIZE = 50;
const RECENT_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 350;

type RecentListCache<T> = { items: T[]; total: number };

function formatDate(ms: number): string {
    try {
        return new Date(ms).toLocaleString();
    } catch {
        return String(ms);
    }
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatReportsPageError(err: unknown, fallback: string): string {
    if (err instanceof LocalServerError) {
        return `${err.message}\nHTTP ${err.status}\n${err.endpoint}`;
    }
    if (err instanceof Error && err.message.trim()) {
        return err.message;
    }
    return fallback;
}

export default function ReportsPage() {
    const { strings } = useLanguage();
    const navigate = useNavigate();

    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [hasLocalServer, setHasLocalServer] = useState<boolean | null>(null);
    const [storageMode, setStorageMode] = useState<PdfWorkflowMode>('manual');
    const hasRemoteStorage = hasLocalServer === true || storageMode === 'cloudDb';

    // Empty search → recent RECENT_LIMIT; non-empty → paginated search (PAGE_SIZE).
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const listSeqRef = useRef(0);
    const recentReportsCacheRef = useRef<RecentListCache<ReportListItem> | null>(null);

    const [items, setItems] = useState<ReportListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string>('');
    const [openingPdfReportId, setOpeningPdfReportId] = useState<string | null>(null);
    const [downloadingPdfReportId, setDownloadingPdfReportId] = useState<string | null>(null);
    const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
    const [reportPendingDelete, setReportPendingDelete] = useState<ReportListItem | null>(null);

    const [settingsSearchInput, setSettingsSearchInput] = useState('');
    const [settingsDebouncedSearch, setSettingsDebouncedSearch] = useState('');
    const settingsListSeqRef = useRef(0);
    const recentSettingsCacheRef = useRef<RecentListCache<SavedSettingListItem> | null>(null);
    const [settingsItems, setSettingsItems] = useState<SavedSettingListItem[]>([]);
    const [settingsTotal, setSettingsTotal] = useState(0);
    const [settingsOffset, setSettingsOffset] = useState(0);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsLoadError, setSettingsLoadError] = useState<string>('');
    const [openingSettingsPdfKey, setOpeningSettingsPdfKey] = useState<string | null>(null);
    const [deletingSettingsKey, setDeletingSettingsKey] = useState<string | null>(null);
    const [settingsPendingDelete, setSettingsPendingDelete] = useState<SavedSettingListItem | null>(null);

    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    navigate('/login');
                    return;
                }
                const profile = await getUserProfile(currentUser.uid);
                if (cancelled) return;
                setUserProfile(profile);

                const ls = await hasConfiguredLocalServer();
                if (cancelled) return;
                setHasLocalServer(ls);

                const mode = await getStorageMode();
                if (cancelled) return;
                setStorageMode(mode);
            } catch (err) {
                logger.error('[ReportsPage] init failed', { err });
            } finally {
                if (!cancelled) {
                    setIsInitialLoad(false);
                }
            }
        };
        void init();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    useEffect(() => {
        if (storageMode !== 'cloudDb') return;
        let cancelled = false;
        void (async () => {
            try {
                await ensureCloudRetentionIfDue();
            } catch (err) {
                if (!cancelled) {
                    logger.warn('[ReportsPage] cloud weekly retention failed', { err });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [storageMode]);

    const role = (userProfile?.role || '').toLowerCase();
    const canFilterByOperator = role === 'manager' || role === 'editor';
    const canDownloadReportPdf = role === 'manager' || role === 'editor';
    const canDeleteReport = role === 'manager' || role === 'editor';
    const canDeleteSettings = role === 'manager' || role === 'editor';
    const canEditSettings = role === 'manager' || role === 'editor';

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        const t = window.setTimeout(() => setSettingsDebouncedSearch(settingsSearchInput), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [settingsSearchInput]);

    const loadPage = useCallback(async (nextOffset: number, searchTerm: string) => {
        if (!hasRemoteStorage) return;
        const q = searchTerm.trim();

        // Recent: restore from cache when available (no extra API / Firestore read).
        if (!q) {
            const cached = recentReportsCacheRef.current;
            if (cached) {
                listSeqRef.current += 1;
                setItems(cached.items);
                setTotal(cached.total);
                setOffset(0);
                setLoadError('');
                setIsLoading(false);
                return;
            }
            const seq = ++listSeqRef.current;
            setIsLoading(true);
            setLoadError('');
            setItems([]);
            setTotal(0);
            try {
                let result: { items: ReportListItem[]; total: number };
                if (storageMode === 'cloudDb') {
                    result = await listCloudReports({ search: '', limit: RECENT_LIMIT, offset: 0 });
                } else {
                    result = await listReports({ search: '', limit: RECENT_LIMIT, offset: 0 });
                }
                if (seq !== listSeqRef.current) return;
                recentReportsCacheRef.current = { items: result.items, total: result.total };
                setItems(result.items);
                setTotal(result.total);
                setOffset(0);
            } catch (err) {
                if (seq !== listSeqRef.current) return;
                const msg = formatReportsPageError(err, 'Failed to load reports');
                logger.error('[ReportsPage] list failed', { err });
                setLoadError(msg);
            } finally {
                if (seq === listSeqRef.current) {
                    setIsLoading(false);
                }
            }
            return;
        }

        const seq = ++listSeqRef.current;
        setIsLoading(true);
        setLoadError('');
        if (nextOffset === 0) {
            setItems([]);
            setTotal(0);
        }
        try {
            let result: { items: ReportListItem[]; total: number };
            if (storageMode === 'cloudDb') {
                result = await listCloudReports({ search: q, limit: PAGE_SIZE, offset: nextOffset });
            } else {
                result = await listReports({ search: q, limit: PAGE_SIZE, offset: nextOffset });
            }
            if (seq !== listSeqRef.current) return;
            setItems(result.items);
            setTotal(result.total);
            setOffset(nextOffset);
        } catch (err) {
            if (seq !== listSeqRef.current) return;
            const msg = formatReportsPageError(err, 'Failed to load reports');
            logger.error('[ReportsPage] list failed', { err });
            setLoadError(msg);
        } finally {
            if (seq === listSeqRef.current) {
                setIsLoading(false);
            }
        }
    }, [hasRemoteStorage, storageMode]);

    useEffect(() => {
        if (!hasRemoteStorage) return;
        void loadPage(0, debouncedSearch);
    }, [hasRemoteStorage, debouncedSearch, loadPage]);

    const loadSettingsPage = useCallback(async (nextOffset: number, searchTerm: string) => {
        if (!hasRemoteStorage) return;
        const q = searchTerm.trim();

        if (!q) {
            const cached = recentSettingsCacheRef.current;
            if (cached) {
                settingsListSeqRef.current += 1;
                setSettingsItems(cached.items);
                setSettingsTotal(cached.total);
                setSettingsOffset(0);
                setSettingsLoadError('');
                setSettingsLoading(false);
                return;
            }
            const seq = ++settingsListSeqRef.current;
            setSettingsLoading(true);
            setSettingsLoadError('');
            setSettingsItems([]);
            setSettingsTotal(0);
            try {
                let result: { items: SavedSettingListItem[]; total: number };
                if (storageMode === 'cloudDb') {
                    result = await listCloudSavedSettings({
                        search: '',
                        limit: RECENT_LIMIT,
                        offset: 0
                    });
                } else {
                    result = await listSavedSettings({
                        search: '',
                        limit: RECENT_LIMIT,
                        offset: 0
                    });
                }
                if (seq !== settingsListSeqRef.current) return;
                recentSettingsCacheRef.current = { items: result.items, total: result.total };
                setSettingsItems(result.items);
                setSettingsTotal(result.total);
                setSettingsOffset(0);
            } catch (err) {
                if (seq !== settingsListSeqRef.current) return;
                const msg = formatReportsPageError(err, 'Failed to load saved settings');
                logger.error('[ReportsPage] saved-settings list failed', { err });
                setSettingsLoadError(msg);
            } finally {
                if (seq === settingsListSeqRef.current) {
                    setSettingsLoading(false);
                }
            }
            return;
        }

        const seq = ++settingsListSeqRef.current;
        setSettingsLoading(true);
        setSettingsLoadError('');
        if (nextOffset === 0) {
            setSettingsItems([]);
            setSettingsTotal(0);
        }
        try {
            let result: { items: SavedSettingListItem[]; total: number };
            if (storageMode === 'cloudDb') {
                result = await listCloudSavedSettings({ search: q, limit: PAGE_SIZE, offset: nextOffset });
            } else {
                result = await listSavedSettings({ search: q, limit: PAGE_SIZE, offset: nextOffset });
            }
            if (seq !== settingsListSeqRef.current) return;
            setSettingsItems(result.items);
            setSettingsTotal(result.total);
            setSettingsOffset(nextOffset);
        } catch (err) {
            if (seq !== settingsListSeqRef.current) return;
            const msg = formatReportsPageError(err, 'Failed to load saved settings');
            logger.error('[ReportsPage] saved-settings list failed', { err });
            setSettingsLoadError(msg);
        } finally {
            if (seq === settingsListSeqRef.current) {
                setSettingsLoading(false);
            }
        }
    }, [hasRemoteStorage, storageMode]);

    useEffect(() => {
        if (!hasRemoteStorage) return;
        void loadSettingsPage(0, settingsDebouncedSearch);
    }, [hasRemoteStorage, settingsDebouncedSearch, loadSettingsPage]);

    const handleClearSearch = () => {
        setSearchInput('');
        setDebouncedSearch('');
    };

    const searchPlaceholder = useMemo(() => {
        const parts = [
            strings.REPORTS_FILTER_DETAIL,
            strings.REPORTS_FILTER_ORDER,
            strings.REPORTS_COL_REVISION
        ];
        if (canFilterByOperator) {
            parts.push(strings.REPORTS_FILTER_OPERATOR);
        }
        return parts.join(', ');
    }, [
        strings.REPORTS_FILTER_DETAIL,
        strings.REPORTS_FILTER_ORDER,
        strings.REPORTS_COL_REVISION,
        strings.REPORTS_FILTER_OPERATOR,
        canFilterByOperator
    ]);

    const handlePrev = () => {
        const next = Math.max(0, offset - PAGE_SIZE);
        if (next !== offset) {
            void loadPage(next, debouncedSearch);
        }
    };

    const handleNext = () => {
        const next = offset + PAGE_SIZE;
        if (next < total) {
            void loadPage(next, debouncedSearch);
        }
    };

    const handleClearSettingsSearch = () => {
        setSettingsSearchInput('');
        setSettingsDebouncedSearch('');
    };

    const settingsSearchPlaceholder = useMemo(
        () =>
            [
                strings.REPORTS_FILTER_DETAIL,
                strings.REPORTS_COL_REVISION,
                strings.SAVED_SETTINGS_COL_EDITOR
            ].join(', '),
        [strings.REPORTS_FILTER_DETAIL, strings.REPORTS_COL_REVISION, strings.SAVED_SETTINGS_COL_EDITOR]
    );

    const handleSettingsPrev = () => {
        const next = Math.max(0, settingsOffset - PAGE_SIZE);
        if (next !== settingsOffset) {
            void loadSettingsPage(next, settingsDebouncedSearch);
        }
    };

    const handleSettingsNext = () => {
        const next = settingsOffset + PAGE_SIZE;
        if (next < settingsTotal) {
            void loadSettingsPage(next, settingsDebouncedSearch);
        }
    };

    const handleDeleteSettingsClick = (row: SavedSettingListItem) => {
        setSettingsPendingDelete(row);
    };

    const cancelDeleteSettings = () => {
        setSettingsPendingDelete(null);
    };

    const confirmDeleteSettings = async () => {
        const row = settingsPendingDelete;
        if (!row) return;

        const rowKey = `${row.detailNo}:${row.revisionId}`;
        setSettingsPendingDelete(null);
        setDeletingSettingsKey(rowKey);
        setSettingsLoadError('');
        const isOnlyRowOnPage = settingsItems.length === 1;
        const backOffset = isOnlyRowOnPage && settingsOffset >= PAGE_SIZE
            ? settingsOffset - PAGE_SIZE
            : settingsOffset;
        try {
            if (storageMode === 'cloudDb') {
                await deleteCloudSavedSettings(row.revisionId);
            } else {
                await deleteRevision(row.detailNo, row.revisionId);
            }
            recentSettingsCacheRef.current = null;
            await loadSettingsPage(backOffset, settingsDebouncedSearch);
        } catch (err) {
            const msg = formatReportsPageError(err, 'Failed to delete saved settings');
            logger.error('[ReportsPage] delete settings failed', { err });
            setSettingsLoadError(msg);
        } finally {
            setDeletingSettingsKey(null);
        }
    };

    const handleOpenSettingsPdf = async (row: SavedSettingListItem) => {
        const rowKey = `${row.detailNo}:${row.revisionId}`;
        const previewWin = window.open('about:blank', '_blank');
        if (!previewWin) {
            setSettingsLoadError(strings.REPORTS_OPEN_PDF_POPUP_BLOCKED);
            return;
        }
        setOpeningSettingsPdfKey(rowKey);
        let objectUrl: string | null = null;
        try {
            if (storageMode === 'cloudDb') {
                const pdfUrl = await fetchCloudSettingsPdfUrl(row.revisionId);
                previewWin.location.replace(pdfUrl);
            } else {
                const blob = await fetchRevisionPdf(row.detailNo, row.revisionId);
                const pdfBlob = blob.type === 'application/pdf' || blob.type === ''
                    ? blob
                    : new Blob([blob], { type: 'application/pdf' });
                objectUrl = URL.createObjectURL(pdfBlob);
                previewWin.location.replace(objectUrl);
                window.setTimeout(() => {
                    if (objectUrl) {
                        URL.revokeObjectURL(objectUrl);
                        objectUrl = null;
                    }
                }, 120_000);
            }
        } catch (err) {
            previewWin.close();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            const msg = formatReportsPageError(err, 'Failed to open PDF');
            logger.error('[ReportsPage] open settings PDF failed', { err });
            setSettingsLoadError(msg);
        } finally {
            setOpeningSettingsPdfKey(null);
        }
    };

    const handleEditSettings = (row: SavedSettingListItem) => {
        const storedReportLanguage = localStorage.getItem('reportLanguage');
        const reportLanguage =
            storedReportLanguage === 'et'
            || storedReportLanguage === 'en'
            || storedReportLanguage === 'ru'
                ? storedReportLanguage
                : 'et';
        const engineerName =
            userProfile?.displayName
            || auth.currentUser?.displayName
            || '';
        navigate('/pdf-viewer', {
            state: {
                detailNo: row.detailNo,
                revisionId: row.revisionId,
                detail: row.detailNo,
                order: undefined,
                role: 'editor' as const,
                excelFile: null,
                editorExcelFile: null,
                engineerName,
                revision: row.revision,
                reportLanguage,
            }
        });
    };

    const handleOpenReportPdf = async (report: ReportListItem) => {
        const previewWin = window.open('about:blank', '_blank');
        if (!previewWin) {
            setLoadError(strings.REPORTS_OPEN_PDF_POPUP_BLOCKED);
            return;
        }

        setOpeningPdfReportId(report.id);
        let objectUrl: string | null = null;
        try {
            if (storageMode === 'cloudDb') {
                const pdfUrl = await fetchCloudReportPdfUrl(report.id);
                previewWin.location.replace(pdfUrl);
            } else {
                const blob = await fetchReportPdf(report.id);
                const pdfBlob = blob.type === 'application/pdf' || blob.type === ''
                    ? blob
                    : new Blob([blob], { type: 'application/pdf' });
                objectUrl = URL.createObjectURL(pdfBlob);
                previewWin.location.replace(objectUrl);
                window.setTimeout(() => {
                    if (objectUrl) {
                        URL.revokeObjectURL(objectUrl);
                        objectUrl = null;
                    }
                }, 120_000);
            }
        } catch (err) {
            previewWin.close();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            const msg = formatReportsPageError(err, 'Failed to open PDF');
            logger.error('[ReportsPage] open PDF failed', { err });
            setLoadError(msg);
        } finally {
            setOpeningPdfReportId(null);
        }
    };

    const handleDownloadReportPdf = async (report: ReportListItem) => {
        setDownloadingPdfReportId(report.id);
        let objectUrl: string | null = null;
        try {
            let blob: Blob;
            if (storageMode === 'cloudDb') {
                // Cross-origin Storage URLs ignore <a download> and open in a tab —
                // fetch as blob first so the object URL triggers a real download.
                const pdfUrl = await fetchCloudReportPdfUrl(report.id);
                const response = await fetch(pdfUrl, { cache: 'no-store' });
                if (!response.ok) {
                    throw new Error(`Failed to download PDF (HTTP ${response.status})`);
                }
                blob = await response.blob();
            } else {
                blob = await fetchReportPdf(report.id);
            }
            const pdfBlob = blob.type === 'application/pdf' || blob.type === ''
                ? blob
                : new Blob([blob], { type: 'application/pdf' });
            objectUrl = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = report.fileName || 'report.pdf';
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            const msg = formatReportsPageError(err, 'Failed to download PDF');
            logger.error('[ReportsPage] download PDF failed', { err });
            setLoadError(msg);
        } finally {
            if (objectUrl) {
                window.setTimeout(() => {
                    if (objectUrl) {
                        URL.revokeObjectURL(objectUrl);
                    }
                }, 1_000);
            }
            setDownloadingPdfReportId(null);
        }
    };

    const handleDeleteReportClick = (report: ReportListItem) => {
        setReportPendingDelete(report);
    };

    const cancelDeleteReport = () => {
        setReportPendingDelete(null);
    };

    const confirmDeleteReport = async () => {
        const report = reportPendingDelete;
        if (!report) return;

        setReportPendingDelete(null);
        setDeletingReportId(report.id);
        setLoadError('');
        const isOnlyRowOnPage = items.length === 1;
        const backOffset = isOnlyRowOnPage && offset >= PAGE_SIZE ? offset - PAGE_SIZE : offset;
        try {
            if (storageMode === 'cloudDb') {
                await deleteCloudReport(report.id);
            } else {
                await deleteReport(report.id);
            }
            recentReportsCacheRef.current = null;
            await loadPage(backOffset, debouncedSearch);
        } catch (err) {
            const msg = formatReportsPageError(err, 'Failed to delete report');
            logger.error('[ReportsPage] delete failed', { err });
            setLoadError(msg);
        } finally {
            setDeletingReportId(null);
        }
    };

    const deleteConfirmMessage = storageMode === 'cloudDb'
        ? strings.REPORTS_DELETE_CONFIRM_CLOUD
        : strings.REPORTS_DELETE_CONFIRM_LOCAL;

    const settingsDeleteConfirmMessage = storageMode === 'cloudDb'
        ? strings.SAVED_SETTINGS_DELETE_CONFIRM_CLOUD
        : strings.SAVED_SETTINGS_DELETE_CONFIRM_LOCAL;

    if (isInitialLoad) {
        return <EngineeringLoadingScreen ariaLabel={strings.PDFVIEWER_LOADING} />;
    }

    if (!userProfile) {
        return (
            <div className="no-organization-container">
                <div className="no-organization-message">
                    {strings.REPORTS_NEED_LOGIN}
                </div>
            </div>
        );
    }

    if (hasLocalServer === false && storageMode !== 'cloudDb') {
        return (
            <div className="no-organization-container">
                <div className="no-organization-message">
                    {strings.REPORTS_NO_LOCAL_SERVER}
                </div>
                <button className="back-button" onClick={() => navigate('/home')}>
                    {strings.BACK_BUTTON}
                </button>
            </div>
        );
    }

    const activeQuery = debouncedSearch.trim();

    // Resolve the page-info template into a localized "{from}–{to} of {total}" string.
    const pageInfoTo = Math.min(offset + PAGE_SIZE, total);
    const pageInfoFrom = total === 0 ? 0 : offset + 1;
    const pageInfo = strings.REPORTS_PAGE_INFO_FORMAT
        .replace('{from}', String(pageInfoFrom))
        .replace('{to}', String(pageInfoTo))
        .replace('{total}', String(total));

    const settingsActiveQuery = settingsDebouncedSearch.trim();
    const settingsPageInfoTo = Math.min(settingsOffset + PAGE_SIZE, settingsTotal);
    const settingsPageInfoFrom = settingsTotal === 0 ? 0 : settingsOffset + 1;
    const settingsPageInfo = strings.REPORTS_PAGE_INFO_FORMAT
        .replace('{from}', String(settingsPageInfoFrom))
        .replace('{to}', String(settingsPageInfoTo))
        .replace('{total}', String(settingsTotal));

    return (
        <div className="reports-page">
            <header className="reports-page-header">
                <h2 id="saved-settings-heading" className="reports-secondary-title">
                    {strings.SAVED_SETTINGS_TITLE}
                </h2>
                <button className="back-button" onClick={() => navigate('/home')}>
                    {strings.BACK_BUTTON}
                </button>
            </header>

            <section aria-labelledby="saved-settings-heading">
                <section className="reports-filters">
                    <div className="reports-search-bar">
                        <input
                            type="search"
                            className="profile-input reports-search-input"
                            value={settingsSearchInput}
                            onChange={(e) => setSettingsSearchInput(e.target.value)}
                            placeholder={settingsSearchPlaceholder}
                            aria-label={settingsSearchPlaceholder}
                        />
                        <button
                            type="button"
                            className="profile-secondary-btn reports-search-icon-btn"
                            onClick={handleClearSettingsSearch}
                            disabled={
                                settingsLoading
                                || (settingsSearchInput === '' && settingsDebouncedSearch === '')
                            }
                            aria-label={strings.REPORTS_RESET_FILTERS}
                            title={strings.REPORTS_RESET_FILTERS}
                        >
                            <svg
                                className="reports-search-icon"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path
                                    fill="currentColor"
                                    d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                                />
                            </svg>
                        </button>
                    </div>
                </section>

                {settingsLoadError ? (
                    <div className="reports-page-error" role="alert">
                        {settingsLoadError}
                    </div>
                ) : null}

                {settingsLoading && settingsItems.length === 0 ? (
                    <div className="reports-empty">
                        {strings.REPORTS_LOADING}
                    </div>
                ) : settingsItems.length === 0 ? (
                    <div className="reports-empty">
                        {strings.SAVED_SETTINGS_EMPTY}
                    </div>
                ) : (
                    <div className="reports-table-wrapper">
                        <table className="reports-table">
                            <thead>
                                <tr>
                                    <th>{strings.REPORTS_COL_DATE}</th>
                                    <th>{strings.REPORTS_COL_DETAIL}</th>
                                    <th>{strings.REPORTS_COL_REVISION}</th>
                                    <th>{strings.SAVED_SETTINGS_COL_STATUS}</th>
                                    <th>{strings.SAVED_SETTINGS_COL_EDITOR}</th>
                                    <th>{strings.REPORTS_COL_SIZE}</th>
                                    <th>{strings.REPORTS_COL_ACTION}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {settingsItems.map((row) => {
                                    const rowKey = `${row.detailNo}:${row.revisionId}`;
                                    return (
                                        <tr key={rowKey}>
                                            <td>{formatDate(row.createdAt)}</td>
                                            <td>{row.detailNo}</td>
                                            <td>{row.revision}</td>
                                            <td>{row.status}</td>
                                            <td>
                                                {row.createdByName || row.createdByUid}
                                            </td>
                                            <td>{formatBytes(row.fileSizeBytes)}</td>
                                            <td>
                                                <div className="reports-action-cell">
                                                    <button
                                                        type="button"
                                                        className="profile-secondary-btn reports-open-pdf-btn"
                                                        onClick={() => void handleOpenSettingsPdf(row)}
                                                        disabled={
                                                            openingSettingsPdfKey === rowKey
                                                            || deletingSettingsKey === rowKey
                                                        }
                                                        aria-label={strings.SAVED_SETTINGS_OPEN_PDF}
                                                        title={strings.SAVED_SETTINGS_OPEN_PDF}
                                                    >
                                                        <svg
                                                            className="reports-open-pdf-icon"
                                                            viewBox="0 0 24 24"
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                                                            />
                                                        </svg>
                                                    </button>
                                                    {canEditSettings && (
                                                        <button
                                                            type="button"
                                                            className="profile-secondary-btn reports-edit-settings-btn"
                                                            onClick={() => handleEditSettings(row)}
                                                            disabled={
                                                                openingSettingsPdfKey === rowKey
                                                                || deletingSettingsKey === rowKey
                                                            }
                                                            aria-label={strings.SAVED_SETTINGS_EDIT}
                                                            title={strings.SAVED_SETTINGS_EDIT}
                                                        >
                                                            <svg
                                                                className="reports-open-pdf-icon"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-hidden="true"
                                                                focusable="false"
                                                            >
                                                                <path d="M12 20h9" />
                                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {canDeleteSettings && (
                                                        <button
                                                            type="button"
                                                            className="member-action-btn delete-btn"
                                                            onClick={() => handleDeleteSettingsClick(row)}
                                                            disabled={
                                                                deletingSettingsKey === rowKey
                                                                || openingSettingsPdfKey === rowKey
                                                            }
                                                            aria-label={strings.DASHBOARD_DELETE}
                                                            title={strings.DASHBOARD_DELETE}
                                                        >
                                                            <svg
                                                                width="20"
                                                                height="20"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-hidden="true"
                                                                focusable="false"
                                                            >
                                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {settingsActiveQuery && settingsTotal > 0 ? (
                    <div className="reports-pagination">
                        <button
                            type="button"
                            className="profile-secondary-btn"
                            onClick={handleSettingsPrev}
                            disabled={settingsOffset === 0 || settingsLoading}
                        >
                            {strings.REPORTS_PREV}
                        </button>
                        <span className="reports-page-info">{settingsPageInfo}</span>
                        <button
                            type="button"
                            className="profile-secondary-btn"
                            onClick={handleSettingsNext}
                            disabled={settingsOffset + PAGE_SIZE >= settingsTotal || settingsLoading}
                        >
                            {strings.REPORTS_NEXT}
                        </button>
                    </div>
                ) : null}
            </section>

            <section className="reports-secondary-section" aria-labelledby="saved-reports-heading">
                <h2 id="saved-reports-heading" className="reports-secondary-title">
                    {strings.REPORTS_TITLE}
                </h2>
                <section className="reports-filters">
                    <div className="reports-search-bar">
                        <input
                            type="search"
                            className="profile-input reports-search-input"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder={searchPlaceholder}
                            aria-label={searchPlaceholder}
                        />
                        <button
                            type="button"
                            className="profile-secondary-btn reports-search-icon-btn"
                            onClick={handleClearSearch}
                            disabled={isLoading || (searchInput === '' && debouncedSearch === '')}
                            aria-label={strings.REPORTS_RESET_FILTERS}
                            title={strings.REPORTS_RESET_FILTERS}
                        >
                            <svg
                                className="reports-search-icon"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path
                                    fill="currentColor"
                                    d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                                />
                            </svg>
                        </button>
                    </div>
                </section>

                {loadError ? (
                    <div className="reports-page-error" role="alert">
                        {loadError}
                    </div>
                ) : null}

                {isLoading && items.length === 0 ? (
                    <div className="reports-empty">
                        {strings.REPORTS_LOADING}
                    </div>
                ) : items.length === 0 ? (
                    <div className="reports-empty">
                        {strings.REPORTS_EMPTY}
                    </div>
                ) : (
                    <div className="reports-table-wrapper">
                        <table className="reports-table">
                            <thead>
                                <tr>
                                    <th>{strings.REPORTS_COL_DATE}</th>
                                    <th>{strings.REPORTS_COL_DETAIL}</th>
                                    <th>{strings.REPORTS_COL_REVISION}</th>
                                    <th>{strings.REPORTS_COL_ORDER}</th>
                                    <th>{strings.REPORTS_COL_MEAS_NR}</th>
                                    <th>{strings.REPORTS_COL_OPERATOR}</th>
                                    <th>{strings.REPORTS_COL_SIZE}</th>
                                    <th>{strings.REPORTS_COL_ACTION}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((report) => (
                                    <tr key={report.id}>
                                        <td>{formatDate(report.savedAt)}</td>
                                        <td>{report.detailNo}</td>
                                        <td>{report.revisionLabel || '—'}</td>
                                        <td>{report.orderNo || '—'}</td>
                                        <td>{report.measurementNumber || '—'}</td>
                                        <td>
                                            {report.operatorName || report.operatorEmail || report.operatorUid}
                                            {report.operatorRole ? ` (${report.operatorRole})` : ''}
                                        </td>
                                        <td>{formatBytes(report.fileSizeBytes)}</td>
                                        <td>
                                            <div className="reports-action-cell">
                                                <button
                                                    type="button"
                                                    className="profile-secondary-btn reports-open-pdf-btn"
                                                    onClick={() => void handleOpenReportPdf(report)}
                                                    disabled={
                                                        openingPdfReportId === report.id
                                                        || downloadingPdfReportId === report.id
                                                        || deletingReportId === report.id
                                                    }
                                                    aria-label={strings.REPORTS_OPEN_REPORT_PDF}
                                                    title={strings.REPORTS_OPEN_REPORT_PDF}
                                                >
                                                    <svg
                                                        className="reports-open-pdf-icon"
                                                        viewBox="0 0 24 24"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                                                        />
                                                    </svg>
                                                </button>
                                                {canDownloadReportPdf && (
                                                    <button
                                                        type="button"
                                                        className="profile-secondary-btn reports-download-pdf-btn"
                                                        onClick={() => void handleDownloadReportPdf(report)}
                                                        disabled={
                                                            downloadingPdfReportId === report.id
                                                            || openingPdfReportId === report.id
                                                            || deletingReportId === report.id
                                                        }
                                                        aria-label={strings.REPORTS_DOWNLOAD_REPORT_PDF}
                                                        title={strings.REPORTS_DOWNLOAD_REPORT_PDF}
                                                    >
                                                        <svg
                                                            className="reports-open-pdf-icon"
                                                            viewBox="0 0 24 24"
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                                {canDeleteReport && (
                                                    <button
                                                        type="button"
                                                        className="member-action-btn delete-btn"
                                                        onClick={() => handleDeleteReportClick(report)}
                                                        disabled={
                                                            deletingReportId === report.id
                                                            || openingPdfReportId === report.id
                                                            || downloadingPdfReportId === report.id
                                                        }
                                                        aria-label={strings.DASHBOARD_DELETE}
                                                        title={strings.DASHBOARD_DELETE}
                                                    >
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <polyline points="3 6 5 6 21 6"></polyline>
                                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeQuery && total > 0 ? (
                    <div className="reports-pagination">
                        <button
                            className="profile-secondary-btn"
                            onClick={handlePrev}
                            disabled={offset === 0 || isLoading}
                        >
                            {strings.REPORTS_PREV}
                        </button>
                        <span className="reports-page-info">
                            {pageInfo}
                        </span>
                        <button
                            className="profile-secondary-btn"
                            onClick={handleNext}
                            disabled={offset + PAGE_SIZE >= total || isLoading}
                        >
                            {strings.REPORTS_NEXT}
                        </button>
                    </div>
                ) : null}
            </section>

            {reportPendingDelete ? (
                <div className="modal-overlay">
                    <div
                        className="modal-container delete-modal"
                        role="alertdialog"
                        aria-labelledby="reports-delete-title"
                        aria-describedby="reports-delete-message"
                    >
                        <div className="modal-header">
                            <h2 id="reports-delete-title">{strings.REPORTS_DELETE_CONFIRM_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text" id="reports-delete-message">
                                {deleteConfirmMessage}{' '}
                                <strong>{reportPendingDelete.fileName}</strong>?
                            </p>
                            <p className="delete-warning-text">
                                {strings.DASHBOARD_DELETE_WARNING}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={cancelDeleteReport}
                                disabled={deletingReportId === reportPendingDelete.id}
                            >
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button
                                type="button"
                                className="btn-delete"
                                onClick={() => void confirmDeleteReport()}
                                disabled={deletingReportId === reportPendingDelete.id}
                            >
                                {strings.DASHBOARD_DELETE}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {settingsPendingDelete ? (
                <div className="modal-overlay">
                    <div
                        className="modal-container delete-modal"
                        role="alertdialog"
                        aria-labelledby="saved-settings-delete-title"
                        aria-describedby="saved-settings-delete-message"
                    >
                        <div className="modal-header">
                            <h2 id="saved-settings-delete-title">
                                {strings.SAVED_SETTINGS_DELETE_CONFIRM_TITLE}
                            </h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text" id="saved-settings-delete-message">
                                {settingsDeleteConfirmMessage}{' '}
                                <strong>
                                    {settingsPendingDelete.detailNo} / {settingsPendingDelete.revision}
                                </strong>?
                            </p>
                            <p className="delete-warning-text">
                                {strings.DASHBOARD_DELETE_WARNING}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={cancelDeleteSettings}
                                disabled={
                                    deletingSettingsKey
                                        === `${settingsPendingDelete.detailNo}:${settingsPendingDelete.revisionId}`
                                }
                            >
                                {strings.DASHBOARD_CANCEL}
                            </button>
                            <button
                                type="button"
                                className="btn-delete"
                                onClick={() => void confirmDeleteSettings()}
                                disabled={
                                    deletingSettingsKey
                                        === `${settingsPendingDelete.detailNo}:${settingsPendingDelete.revisionId}`
                                }
                            >
                                {strings.DASHBOARD_DELETE}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
